import { useState, useEffect, useRef } from "react";
import { RoxxeAwake, RoxxeSleep, RoxxeLogo } from "./RoxxeArt";

// ─────────────────────────────────────────────────────────────────────────────
// Roxxe's personality lives server-side in api/chat/soul.js.
// This file is just the stage: gate, chat, and the dog.
// ─────────────────────────────────────────────────────────────────────────────

const MSGS_KEY = "rx_msgs_v1";
const MODE_KEY = "rx_mode";

const store = {
  get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
  del(k) { try { sessionStorage.removeItem(k); } catch { /* private mode */ } },
};

const SUGGESTIONS = [
  "My coworker microwaves fish in the office every single day.",
  "I have 63 browser tabs open and I'm scared to close any of them.",
  "Should I text my ex happy birthday?",
  "What's going on in the news today?",
  "I bought a treadmill in January. It's now a coat rack.",
];

const THINKING = [
  "Roxxe is judging you…",
  "Sniffing around the internet…",
  "Picking the right movie…",
  "Deciding if you deserve help…",
  "Sighing loudly…",
];

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Alfa+Slab+One&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;1,400&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#0e0b09;--bg2:#16110d;
  --text:#f5ecdf;--mid:rgba(245,236,223,.84);--dim:rgba(245,236,223,.5);--faint:rgba(245,236,223,.16);
  --amber:#f4a93c;--amber-hi:#ffc766;--ad:rgba(244,169,60,.16);--af:rgba(244,169,60,.07);
  --red:#c8372d;--red-d:rgba(200,55,45,.18);
  --b:rgba(244,169,60,.12);--b2:rgba(244,169,60,.24);--b3:rgba(244,169,60,.42);
  --slab:'Alfa Slab One',Georgia,serif;--sans:'Instrument Sans',system-ui,sans-serif;--mono:'IBM Plex Mono',ui-monospace,monospace;
}
html,body{height:100%;background:var(--bg);overflow:hidden}
button{font:inherit}

/* ── stage dressing ── */
.grain{position:fixed;inset:0;pointer-events:none;z-index:999;opacity:.03;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:256px}
.spot{position:fixed;inset:0;pointer-events:none;z-index:0;
  background:radial-gradient(ellipse 38% 52% at 50% 28%,rgba(255,206,130,.11) 0%,rgba(255,206,130,.04) 45%,transparent 72%)}
.curtain{position:fixed;top:0;bottom:0;width:7vw;max-width:90px;pointer-events:none;z-index:1;
  background:repeating-linear-gradient(90deg,#3e0d0a 0 12px,#5e1611 12px 20px,#4a100c 20px 30px)}
.curtain.l{left:0;-webkit-mask:linear-gradient(90deg,#000 20%,transparent);mask:linear-gradient(90deg,#000 20%,transparent)}
.curtain.r{right:0;-webkit-mask:linear-gradient(270deg,#000 20%,transparent);mask:linear-gradient(270deg,#000 20%,transparent)}

/* ── dog animation ── */
@keyframes breathe{0%,100%{transform:scaleY(1) scaleX(1)}48%{transform:scaleY(1.035) scaleX(.985)}}
.dog-sleep-inner{animation:breathe 4.2s ease-in-out infinite;transform-origin:bottom center;width:100%;height:100%}
.dog-sleep{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transition:opacity .5s ease,transform .5s ease}
.dog-sleep.waking{opacity:0;transform:scale(.9) translateY(10px)}
.dog-awake{position:absolute;inset:0;display:flex;align-items:flex-end;justify-content:center;opacity:0;
  transform:translateY(26px) scale(.92);pointer-events:none;
  transition:opacity .7s .2s cubic-bezier(.34,1.3,.64,1),transform .8s .2s cubic-bezier(.34,1.3,.64,1)}
.dog-awake.waking{opacity:1;transform:none}
@keyframes zz{0%{opacity:0;transform:translate(0,4px)}30%{opacity:.8}100%{opacity:0;transform:translate(6px,-10px)}}
.zz{opacity:0;animation:zz 3s ease-in-out infinite}
.z2{animation-delay:.9s}.z3{animation-delay:1.8s}
.lid-closed{transform:scaleY(1)}
@keyframes lidOpen{from{transform:scaleY(1)}to{transform:scaleY(0)}}
.lid-opening{animation:lidOpen .45s ease-out forwards}
@keyframes lidBlink{0%,82%,100%{transform:scaleY(0)}88%{transform:scaleY(1)}94%{transform:scaleY(.08)}}
.lid-blinking{transform:scaleY(0);animation:lidBlink 5.6s .6s infinite}
@keyframes wag{0%,100%{transform:rotate(0)}50%{transform:rotate(-7deg)}}
.tail-wag{animation:wag 1.6s ease-in-out infinite}

/* ── GATE ── */
.gate{position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
  z-index:10;padding:24px 16px;animation:fadeIn .7s ease both;font-family:var(--sans);color:var(--text)}
.gate.exit{animation:gOut .5s ease forwards}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes gOut{to{opacity:0;transform:translateY(-14px)}}
.gate-dog{position:relative;width:230px;height:200px;flex-shrink:0;margin-bottom:10px}
.gate-quip{font-family:var(--mono);font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:rgba(244,169,60,.65);
  min-height:18px;margin-bottom:10px;opacity:0}
.gate-quip.show{animation:qIn .45s .9s ease forwards}
@keyframes qIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
.marquee{position:relative;padding:14px 34px 12px;border:1px solid var(--b2);border-radius:6px;
  background:linear-gradient(180deg,rgba(244,169,60,.05),rgba(244,169,60,.015));margin-bottom:14px;text-align:center}
.bulbs{display:flex;justify-content:space-between;gap:10px;position:absolute;left:14px;right:14px}
.bulbs.top{top:-5px}.bulbs.bot{bottom:-5px}
.bulb{width:9px;height:9px;border-radius:50%;background:var(--amber-hi);opacity:.3;animation:bulb 1.6s ease-in-out infinite}
@keyframes bulb{0%,100%{opacity:.28;box-shadow:none}50%{opacity:1;box-shadow:0 0 10px 2px rgba(255,199,102,.55)}}
.gate-name{font-family:var(--slab);font-size:64px;line-height:1;letter-spacing:.06em;color:var(--amber);
  text-shadow:0 0 28px rgba(244,169,60,.28),0 2px 0 #6b3a0c}
.gate-now{font-family:var(--mono);font-size:10px;letter-spacing:.24em;text-transform:uppercase;color:var(--dim);margin-top:8px}
.gate-tag{font-style:italic;font-size:16px;color:var(--mid);margin-bottom:24px;text-align:center}
.ticket{--n:9px;background:var(--amber);color:#1a0f05;border:none;cursor:pointer;padding:14px 40px;
  font-family:var(--mono);font-weight:500;font-size:12px;letter-spacing:.24em;text-transform:uppercase;
  display:flex;flex-direction:column;align-items:center;gap:3px;transition:transform .15s,background .15s;
  -webkit-mask:radial-gradient(circle var(--n) at 0 50%,transparent 97%,#000) left/51% 100% no-repeat,radial-gradient(circle var(--n) at 100% 50%,transparent 97%,#000) right/51% 100% no-repeat;
  mask:radial-gradient(circle var(--n) at 0 50%,transparent 97%,#000) left/51% 100% no-repeat,radial-gradient(circle var(--n) at 100% 50%,transparent 97%,#000) right/51% 100% no-repeat}
.ticket small{font-size:9px;letter-spacing:.3em;opacity:.65}
.ticket:hover{background:var(--amber-hi);transform:translateY(-2px) rotate(-1deg)}
.ticket:focus-visible{outline:2px solid var(--amber-hi);outline-offset:4px}
.gate-foot{margin-top:22px;font-family:var(--mono);font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:var(--faint)}

/* ── APP ── */
.app{position:relative;z-index:2;height:100vh;height:100dvh;display:flex;flex-direction:column;
  font-family:var(--sans);color:var(--text);max-width:880px;margin:0 auto;animation:aIn .6s ease both}
@keyframes aIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.banner{display:flex;align-items:center;gap:9px;padding:8px 28px;border-bottom:1px solid var(--b);
  font-family:var(--mono);font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:rgba(244,169,60,.6);flex-shrink:0}
.rec{width:6px;height:6px;border-radius:50%;background:var(--red);animation:pulse 2s infinite}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
.hdr{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 28px;border-bottom:1px solid var(--b);flex-shrink:0}
.hdr-l{display:flex;align-items:center;gap:14px;min-width:0}
.hdr-logo{background:none;border:none;cursor:pointer;padding:0;display:flex;transition:transform .2s}
.hdr-logo:hover{transform:rotate(-6deg)}
.hdr-name{font-family:var(--slab);font-size:26px;letter-spacing:.06em;color:var(--amber);line-height:1}
.hdr-sub{font-size:13px;color:var(--dim);margin-top:4px}
.hdr-status{display:flex;align-items:center;gap:7px;font-family:var(--mono);font-size:10px;letter-spacing:.12em;
  text-transform:uppercase;color:var(--dim);margin-top:5px}
.gdot{width:6px;height:6px;border-radius:50%;background:#6fbf6a;animation:pulse 2.6s infinite}
.hbtn{background:none;border:1px solid var(--b2);color:var(--dim);border-radius:4px;padding:7px 14px;cursor:pointer;
  font-family:var(--mono);font-size:10px;letter-spacing:.16em;text-transform:uppercase;transition:all .15s;white-space:nowrap}
.hbtn:hover{color:var(--text);border-color:var(--b3);background:var(--af)}

.msgs{flex:1;overflow-y:auto;padding:34px 0 16px;scrollbar-width:thin;scrollbar-color:rgba(244,169,60,.12) transparent}
.empty{padding:28px 28px 0}
.empty-hl{font-family:var(--slab);font-size:34px;line-height:1.15;color:rgba(245,236,223,.2)}
.empty-sub{font-style:italic;font-size:16px;color:var(--dim);margin-top:8px}
.chips{display:flex;flex-direction:column;gap:9px;margin-top:28px;align-items:flex-start}
.chip{background:var(--af);border:1px solid var(--b);border-left:3px solid var(--b3);color:var(--mid);text-align:left;
  font-size:15px;padding:10px 16px;border-radius:3px;cursor:pointer;transition:all .15s;max-width:100%}
.chip:hover{color:var(--text);border-color:var(--b2);border-left-color:var(--amber);background:var(--ad);transform:translateX(3px)}

.mrow{padding:0 28px;margin-bottom:26px;animation:mIn .3s ease both}
@keyframes mIn{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}
.mmeta{display:flex;align-items:center;gap:9px;font-family:var(--mono);font-size:10px;letter-spacing:.2em;text-transform:uppercase;margin-bottom:8px}
.mmeta .ln{width:16px;height:1px}
.mmeta.you{color:var(--dim)}.mmeta.you .ln{background:var(--faint)}
.mmeta.rox{color:var(--amber)}.mmeta.rox .ln{background:var(--b3)}
.mbody{font-size:17.5px;line-height:1.7;max-width:680px;white-space:pre-wrap;overflow-wrap:anywhere}
.mbody.you{color:var(--mid)}
.mbody.rox{color:var(--text);padding-left:14px;border-left:2px solid var(--b2)}
.mbody.err{color:rgba(230,130,120,.9);border-left-color:var(--red-d)}
.srcs{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;padding-left:14px}
.src{font-family:var(--mono);font-size:10px;letter-spacing:.06em;color:rgba(244,169,60,.6);border:1px solid var(--b);border-radius:3px;padding:3px 8px}
.thinking{padding:0 28px;margin-bottom:26px}
.tline{display:flex;align-items:center;gap:12px;padding-left:14px;border-left:2px solid var(--b)}
.tdots{display:flex;gap:5px}
.tdots span{width:6px;height:6px;border-radius:50%;background:var(--amber);animation:td 1.1s infinite}
.tdots span:nth-child(2){animation-delay:.18s}.tdots span:nth-child(3){animation-delay:.36s}
@keyframes td{0%,60%,100%{transform:scale(.7);opacity:.3}30%{transform:scale(1.1);opacity:1}}
.ttext{font-family:var(--mono);font-size:11px;letter-spacing:.1em;color:var(--dim)}

.iarea{padding:14px 28px 18px;border-top:1px solid var(--b);flex-shrink:0}
.iwrap{display:flex;gap:10px;align-items:flex-end}
.ibox{flex:1;background:rgba(244,169,60,.035);border:1px solid var(--b2);border-radius:6px;color:var(--text);
  font-family:var(--sans);font-size:16.5px;line-height:1.55;padding:12px 16px;resize:none;outline:none;
  min-height:50px;max-height:160px;transition:border-color .2s}
.ibox:focus{border-color:var(--b3)}
.ibox::placeholder{color:var(--dim);font-style:italic}
.send{width:50px;height:50px;border:none;border-radius:6px;background:var(--amber);color:#1a0f05;font-size:20px;font-weight:600;
  cursor:pointer;flex-shrink:0;transition:all .15s;display:flex;align-items:center;justify-content:center}
.send:hover:not(:disabled){background:var(--amber-hi);transform:translateY(-1px)}
.send:disabled{opacity:.28;cursor:not-allowed}
.ihint{margin-top:7px;font-family:var(--mono);font-size:10px;letter-spacing:.1em;color:var(--faint)}

@media(max-width:600px){
  .curtain{width:14px}
  .gate-dog{width:180px;height:156px}
  .gate-name{font-size:48px}
  .marquee{padding:12px 22px 10px}
  .gate-now{font-size:9px;letter-spacing:.16em}
  .gate-tag{font-size:15px;margin-bottom:20px}
  .banner{padding:6px 16px;font-size:9px;letter-spacing:.12em}
  .hdr{padding:10px 16px}
  .hdr-logo svg{width:44px!important;height:39px!important}
  .hdr-name{font-size:21px}
  .hdr-sub{display:none}
  .hbtn{padding:6px 10px;font-size:9px}
  .msgs{padding:20px 0 10px}
  .empty{padding:18px 16px 0}
  .empty-hl{font-size:26px}
  .chip{font-size:14.5px}
  .mrow,.thinking{padding:0 16px;margin-bottom:20px}
  .mbody{font-size:16px;line-height:1.62}
  .iarea{padding:10px 16px max(12px,env(safe-area-inset-bottom))}
  .ibox{font-size:16px}
  .ihint{display:none}
}
@media(prefers-reduced-motion:reduce){*{animation-duration:.01ms!important;animation-iteration-count:1!important}}
`;

// ─────────────────────────────────────────────────────────────────────────────
// API — the browser only sends the conversation; the soul is added server-side
// ─────────────────────────────────────────────────────────────────────────────
async function askRoxxe(history) {
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages: history }),
  });
  let data = {};
  try { data = await res.json(); } catch { /* non-JSON error page */ }
  if (!res.ok || data.error) throw new Error(data.error || `Something broke (${res.status}). Try again.`);
  return { text: data.text, sources: data.sources || [] };
}

// ─────────────────────────────────────────────────────────────────────────────
// GATE — Roxxe asleep, then very reluctantly awake
// ─────────────────────────────────────────────────────────────────────────────
function Gate({ onEnter }) {
  const [waking, setWaking] = useState(false);
  const [eyeState, setEyeState] = useState("closed");
  const [quip, setQuip] = useState(false);
  const [exit, setExit] = useState(false);

  const enter = () => {
    if (waking) return;
    setWaking(true);
    setTimeout(() => setEyeState("opening"), 300);
    setTimeout(() => setEyeState("blinking"), 1000);
    setTimeout(() => setQuip(true), 700);
    setTimeout(() => setExit(true), 2500);
    setTimeout(onEnter, 3000);
  };

  return (
    <div className={`gate ${exit ? "exit" : ""}`}>
      <div className="gate-dog">
        <div className={`dog-sleep ${waking ? "waking" : ""}`}><div className="dog-sleep-inner"><RoxxeSleep/></div></div>
        <div className={`dog-awake ${waking ? "waking" : ""}`}><RoxxeAwake eyeState={eyeState}/></div>
      </div>
      <div className={`gate-quip ${quip ? "show" : ""}`}>Oh. It's you.</div>

      <div className="marquee">
        <div className="bulbs top">{Array.from({ length: 12 }, (_, i) => <span key={i} className="bulb" style={{ animationDelay: `${i * 0.13}s` }}/>)}</div>
        <div className="gate-name">ROXXE</div>
        <div className="gate-now">Now showing · A Dakota Sport Retriever with opinions</div>
        <div className="bulbs bot">{Array.from({ length: 12 }, (_, i) => <span key={i} className="bulb" style={{ animationDelay: `${(11 - i) * 0.13}s` }}/>)}</div>
      </div>
      <div className="gate-tag">Bring your problems. She'll bring the side-eye.</div>

      <button className="ticket" onClick={enter} autoFocus>
        <small>Admit one</small>
        Wake her up
      </button>
      <div className="gate-foot">Web aware · Judging since birth</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT
// ─────────────────────────────────────────────────────────────────────────────
function Chat({ onLeave }) {
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(store.get(MSGS_KEY) || "[]"); } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const endRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { store.set(MSGS_KEY, JSON.stringify(messages.slice(-60))); }, [messages]);
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setTick((n) => n + 1), 1900);
    return () => clearInterval(t);
  }, [loading]);
  useEffect(() => {
    const el = boxRef.current; if (!el) return;
    el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, [input]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setLoading(true); setTick(0);
    try {
      const history = next.filter((m) => !m.err).map(({ role, content }) => ({ role, content }));
      const { text: reply, sources } = await askRoxxe(history);
      setMessages((m) => [...m, { role: "assistant", content: reply, sources }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: err.message, err: true }]);
    }
    setLoading(false);
    boxRef.current?.focus();
  };

  const clear = () => { setMessages([]); store.del(MSGS_KEY); };

  return (
    <div className="app">
      <div className="banner"><span className="rec"/>Now showing · Your life choices · Web aware</div>

      <header className="hdr">
        <div className="hdr-l">
          <button className="hdr-logo" onClick={onLeave} title="Back to the lobby" aria-label="Back to the lobby"><RoxxeLogo size={58}/></button>
          <div>
            <div className="hdr-name">ROXXE</div>
            <div className="hdr-sub">Dakota Sport Retriever · Professional side-eye</div>
            <div className="hdr-status"><span className="gdot"/>Judging you from across the room</div>
          </div>
        </div>
        {messages.length > 0 && <button className="hbtn" onClick={clear}>New scene</button>}
      </header>

      <div className="msgs">
        {messages.length === 0 && (
          <div className="empty">
            <div className="empty-hl">Go ahead.<br/>Tell her what happened.</div>
            <div className="empty-sub">She's heard worse. Probably.</div>
            <div className="chips">
              {SUGGESTIONS.map((s) => <button key={s} className="chip" onClick={() => send(s)}>{s}</button>)}
            </div>
          </div>
        )}

        {messages.map((m, i) => {
          const you = m.role === "user";
          return (
            <div key={i} className="mrow">
              <div className={`mmeta ${you ? "you" : "rox"}`}><span className="ln"/>{you ? "You" : "Roxxe"}</div>
              <div className={`mbody ${you ? "you" : "rox"} ${m.err ? "err" : ""}`}>{m.content}</div>
              {!you && m.sources?.length > 0 && (
                <div className="srcs">{m.sources.map((s, si) => <span key={si} className="src">⌕ sniffed: {s}</span>)}</div>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="thinking">
            <div className="mmeta rox"><span className="ln"/>Roxxe</div>
            <div className="tline">
              <div className="tdots"><span/><span/><span/></div>
              <div className="ttext">{THINKING[tick % THINKING.length]}</div>
            </div>
          </div>
        )}
        <div ref={endRef}/>
      </div>

      <div className="iarea">
        <div className="iwrap">
          <textarea ref={boxRef} className="ibox" rows={1} maxLength={4000}
            placeholder="Tell Roxxe what happened…"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}/>
          <button className="send" onClick={() => send()} disabled={loading || !input.trim()} aria-label="Send">↑</button>
        </div>
        <div className="ihint">Enter to send · Shift+Enter for a new line · She knows what's in the news</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function Root() {
  const [inside, setInside] = useState(() => store.get(MODE_KEY) === "chat");
  const enter = () => { store.set(MODE_KEY, "chat"); setInside(true); };
  const leave = () => { store.del(MODE_KEY); setInside(false); };
  return (
    <>
      <style>{css}</style>
      <div className="grain"/><div className="spot"/>
      <div className="curtain l"/><div className="curtain r"/>
      {inside ? <Chat onLeave={leave}/> : <Gate onEnter={enter}/>}
    </>
  );
}
